

import React, { Fragment, useState, useContext } from 'react';


// Utils

import { processAsJson } from '@utils/miscellaneous/processAsJson';
import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';

// Context

import { FullDataContext } from '@contexts/documentsVerificationContexts';
import { getContext } from '@contexts/documentsVerificationContexts';

// Constants

import { checkIfConfigFails, getVerificationDocumentsConfig } from '@utils/miscellaneous/getVerificationDocumentsConfig';
import { documentsVerificationConfig } from '@constants/configs/QLEDocumentVerificationConfigs/documentVerificationConfig';
import { UPLOADED_DOCUMENT_STATUSES } from '@constants/common/miscellaneous';
import { omit } from '@utils/miscellaneous/objectHelpers';





const UploadHyperDashboard = props => {

    const {
        allDocumentsAreProvided,
        applicantInfo,
        applicantInfo: {
            applicantGuid: currentApplicantGuid,
            event
        },
        isExtended,
        isSuperUser,
        parentActions: {
            updateMockData,
            updateEnvironmentProp
        },
        stateCode
    } = props;

    const fullData = useContext(FullDataContext);

    const [fileInfo] = useContext(getContext('fileInfo')) as any;

    const [ backEndDataView, switchBackEndData ] = useState('Full Data');

    const [ uiConfigDataView, switchUiConfigData ] = useState('Config for Event');

    const [
        views,
        toggleViews
    ] = useState({
        isUiConfigViewShown: false,
        isUploadViewShown: false,
        isBackEndDataShown: false,
        isJsonInjectorShown: false,
        isProcessingShown: true
    });

    const {
        isUiConfigViewShown,
        isUploadViewShown,
        isBackEndDataShown,
        isJsonInjectorShown,
        isProcessingShown
    } = views;

    const [ json, updateJson ] = useState('');

    const inlineStyles = {
        commonForSections: {
            fontSize: '1.2rem',
            width: '600px',
            padding: '1rem'
        },
        uploadSection: {
            height: '200px',
            border: '1px solid yellow'
        },
        uiConfigSection: {
            height: '30%',
            border: '1px solid red'
        },
        backEndDataSection: {
            height: '30%',
            border: '1px solid green'
        },
        button: {
            display: 'inline-block',
            fontSize: '1.3rem',
            padding: '1px 8px',
            borderRadius: '3px'
        },
        extraWide: {
            width: '600px'
        },
        label: {
            fontSize: '1.2rem',
            fontWeight: 'bold'
        }
    };

    const transformUiConfig = (config, stateCode) => {
        return [ ...config[stateCode]];
    };

    const stringify = payload => JSON.stringify(payload, null, 2);

    const handleJsonChange = e => {
        const {
            target: {
                value
            }
        } = e;

        updateJson(value);
    };

    const getDataButtons = () => getButtons(
        [
            'Applicant Data',
            'Grouped Data',
            'Full Data'
        ],
        backEndDataView,
        switchBackEndData,
        views.isBackEndDataShown
    );
    
    const getUiConfigButtons = () => getButtons(
        [
            'Config for State',
            'Config for Event'
        ],
        uiConfigDataView,
        switchUiConfigData,
        views.isUiConfigViewShown
    );

    const getUserSwitchButtons = () => getButtons(
        [
            'SuperUser',
            'Customer'
        ],
        isSuperUser ? 'SuperUser' : 'Customer',
        value => updateEnvironmentProp('user.role', value === 'SuperUser' ? 'ADMIN' : 'INDIVIDUAL'),
        true
    );

    const getButtons = (buttons, isShown, onClickAction, isSectionShown) => {
        return buttons.map(button => (
            <button
                style = {{
                    ...inlineStyles.button,
                    ...(
                        isSectionShown ?
                            {
                                backgroundColor: button === isShown ? 'green' : 'initial',
                                color: button === isShown ? 'white' : 'initial'
                            } :
                            {}
                    ),
                }}
                key = { button }
                name = { button }
                // @ts-ignore
                onClick = { ({ target: { name } }) => { onClickAction(name); }}
            >
                { button }
            </button>
        ));
    };
    
    const getUiConfigDataView = () => {

        switch(uiConfigDataView) {
            case 'Config for State':
                return (
                    <Fragment>
                        <b>State: { stateCode }</b>
                        <br/>
                        <br/>
                        { stringify(transformUiConfig(documentsVerificationConfig, stateCode)) }
                    </Fragment>
                );

            case 'Config for Event':
                return (
                    <Fragment>
                        <b>Event: { event }</b>
                        <br/>
                        <br/>
                        { stringify(!checkIfConfigFails(stateCode, event) && getVerificationDocumentsConfig(stateCode, event)) }
                    </Fragment>
                );
        }
    };

    const getBackEndDataView = () => {

        const topLevelData = omit(fullData, [ 'applicants' ]);

        switch(backEndDataView) {
            case 'Applicant Data':
                return (
                    <Fragment>
                        Applicant data:
                        <br/>
                        { stringify(applicantInfo) }
                    </Fragment>
                );

            case 'Grouped Data':
                return (
                    <Fragment>
                        Meta data:
                        <br/>
                        { stringify({ ...topLevelData, ...omit(applicantInfo, [ 'documents' ]) }) }
                        <br/>
                        Documents Info:
                        <br/>
                        { stringify(applicantInfo.documents) }
                    </Fragment>
                );
            default:
                return (
                    <Fragment>
                        Full Data:
                        <br/>
                        { stringify(fullData) }
                    </Fragment>
                );
        }
    };

    const getSectionHeader = (label, view, buttons?) => {

        const isShown = views[view];

        return (
            <div
                className = 'grid-row'
                style = {{
                    ...inlineStyles.extraWide
                }}
            >
                <span
                    className = 'grid-col-3'
                    // @ts-ignore
                    style = {{
                        ...inlineStyles.label
                    }}
                >
                    { label }
                </span>
                {
                    view &&

                    <span className = 'grid-col-2'>
                        <button
                            style = {{
                                ...inlineStyles.button,
                                color: isShown ? 'red' : 'green'
                            }}
                            onClick = { () => toggleViews(views => ({ ...views, [ view ]: !isShown })) }
                        >
                            { isShown ? 'Hide' : 'Show' }
                        </button>
                    </span>
                }
                <span className = 'grid-col-7'>
                    { buttons }
                </span>
            </div>
        );
    };

    const handleJsonUpload = () => {

        const DEFAULT_STATE = {
            applicants: [],
            caseNumber: ''
        };

        const checkPayload = data => {
            if(data.payload) {
                if(data.payload.caseNumber) {
                    return data.payload;
                } else {
                    alert('JSON with =payload= property doesn\'t contain =caseNumber= property');
                    return fullData;
                }
            } else {
                if(data.caseNumber) {
                    return data;
                } else {
                    alert('JSON doesn\'t contain =caseNumber= property');
                    return fullData;
                }
            }
        };

        updateMockData('inject', checkPayload(processAsJson(json, DEFAULT_STATE)));
        updateJson('');
    };



    const getApprovalRejectionDashboard = () => {

        const handleSuperUserAction = e => {
            const payload = JSON.parse(e.target.dataset.info);
            updateMockData('processTicket', payload);
        };

        const superUserButtonsConfig = [
            {
                buttonText: 'Verify',
                documentStatus: UPLOADED_DOCUMENT_STATUSES.VERIFIED,

            },
            {
                buttonText: 'Reject',
                documentStatus: UPLOADED_DOCUMENT_STATUSES.REJECTED,

            }
        ];

        const getSuperUserButtons = info => {
            return superUserButtonsConfig.map(b => {
                const { buttonText, documentStatus } = b;
                return (
                    <button
                        key = { buttonText }
                        style = {{ ...inlineStyles.button }}
                        onClick = { handleSuperUserAction }
                        data-info = { JSON.stringify({ ...info, documentStatus }) }
                    >
                        { buttonText }
                    </button>
                );
            });
        };

        // @ts-ignore
        return fullData.applicants.map(a => {

            const { applicantGuid, documents, name } = a;

            if(currentApplicantGuid !== applicantGuid) {
                return null;
            }

            return (
                <div
                    key = { applicantGuid }
                    style = {{
                        ...inlineStyles.extraWide,
                        border: '1px solid grey',
                        borderRadius: '0.5rem',
                        margin: '1rem',
                        padding: '1rem'
                    }}
                >
                    <div><b>{ normalizeNameOfPerson(name) }</b></div>
                    <br/>
                    <div>
                        {
                            documents.map(c => {
                                const { categoryType, documentTypes } = c;

                                return (
                                    <div key = { categoryType }>
                                        <div
                                            //@ts-ignore
                                            style = {{ ...inlineStyles.label, color: 'orange' }}
                                        >
                                            { categoryType }
                                        </div>
                                        {
                                            documentTypes.map(dt => {
                                                const { uploadedFileName, documentType, documentStatus } = dt;

                                                const info = {
                                                    applicantGuid,
                                                    categoryType,
                                                    documentType,
                                                    uploadedFileName
                                                };

                                                return (
                                                    documentStatus === UPLOADED_DOCUMENT_STATUSES.SUBMITTED ?
                                                        <div key = { uploadedFileName } style = {{ height: '4rem' }}>
                                                            <span>
                                                                <span
                                                                    //@ts-ignore
                                                                    style = {{ ...inlineStyles.label }}
                                                                >
                                                                    { documentType }
                                                                </span> &nbsp;
                                                                <span
                                                                    //@ts-ignore
                                                                    style = {{
                                                                        ...inlineStyles.label,
                                                                        color: 'green'
                                                                    }}
                                                                >
                                                                    { uploadedFileName }
                                                                </span> &nbsp;
                                                            </span>
                                                            <span className = 'pull-right'>
                                                                { getSuperUserButtons(info) }
                                                            </span>
                                                        </div>:
                                                        null
                                                );
                                            })
                                        }
                                    </div>
                                );
                            })
                        }
                    </div>
                </div>
            );
        } );
    };

    return (
        <div className = 'tablet:grid-col-2 padding-2'>
            {
                isExtended &&

                <Fragment>
                    {
                        <Fragment>
                            <b style = {{ position: 'relative' }}>
                                <span>
                                    Validation {allDocumentsAreProvided
                                        ? <span style={{ color: 'green' }}>Completed</span> :
                                        <span style={{ color: 'red' }}>Incomplete</span>
                                    }
                                </span>
                                <h5
                                    style={{
                                        position: 'absolute',
                                        right: '-450px',
                                        top: '10px',
                                        color: 'darkred'
                                    }}
                                >
                                    In debug mode always upload documents with different names!
                                </h5>
                            </b>
                            <br/>
                            <br/>
                        </Fragment>
                    }

                    {
                        getSectionHeader(
                            'User switching',
                            null,
                            getUserSwitchButtons()
                        )
                    }

                    {
                        getSectionHeader(
                            'JSON Injector',
                            'isJsonInjectorShown',
                            <button
                                style = {{
                                    ...inlineStyles.button
                                }}
                                key = { 'button' }
                                name = { 'jsonInjector' }
                                onClick = { handleJsonUpload }
                            >
                                Upload Mock
                            </button>
                        )
                    }

                    {
                        isJsonInjectorShown &&

                            <div
                                style = {{
                                    ...inlineStyles.commonForSections
                                }}
                            >
                                <textarea
                                    style={{
                                        width: '100%'
                                    }}
                                    rows = { 10 }
                                    placeholder = 'Paste Mock JSON here...'
                                    value = { json }
                                    onChange = { handleJsonChange }
                                />
                            </div>
                    }

                    {
                        getSectionHeader(
                            'File info:',
                            'isUploadViewShown'
                        )
                    }

                    {
                        isUploadViewShown &&

                        <pre
                            style = {{
                                ...inlineStyles.commonForSections,
                                ...inlineStyles.uploadSection
                            }}
                        >
                            { stringify(fileInfo) }
                        </pre>

                    }

                    {
                        getSectionHeader(
                            'UI Configuration:',
                            'isUiConfigViewShown',
                            getUiConfigButtons()
                        )
                    }

                    {
                        isUiConfigViewShown &&

                        <pre
                            style = {{
                                ...inlineStyles.commonForSections,
                                ...inlineStyles.uiConfigSection
                            }}
                        >
                            { getUiConfigDataView() }
                        </pre>
                    }

                    {
                        getSectionHeader(
                            'Data received from BE:',
                            'isBackEndDataShown',
                            getDataButtons()
                        )
                    }

                    {
                        isBackEndDataShown &&

                        <pre
                            style = {{
                                ...inlineStyles.commonForSections,
                                ...inlineStyles.backEndDataSection
                            }}
                        >
                            { getBackEndDataView() }
                        </pre>

                    }

                    {
                        getSectionHeader(
                            'Process Documents:',
                            'isProcessingShown'
                        )
                    }

                    {
                        isProcessingShown &&

                            getApprovalRejectionDashboard()

                    }

                </Fragment>
            }

        </div>
    );

};


export default UploadHyperDashboard;